import { Link, useNavigate } from "react-router";
import { get, dev } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { Video, VideoList } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { Bar, Dot, ErrorLine, VidFrame } from "../components/ui";
import { fmtDate, fmtDuration } from "../lib/format";
import { videoStatus } from "../lib/status";
import { useIsDesktop } from "../lib/device";
import { useState } from "react";

/** 02 · My videos: B grid (≥1 upload) and C empty state. Status is one line and a hairline; Upload is the only colour. */
export default function Videos() {
  const { me, plan, refresh } = useAuth();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const list = useLoad(() => get<VideoList>("/v1/videos?limit=60"), []);
  const items = list.data?.items ?? [];
  usePoll(() => Promise.all([list.reload(), refresh()]).then(() => undefined), 8000, items.some((v) => !videoStatus(v, plan).terminal));
  const [seeding, setSeeding] = useState(false);
  const usage = me?.usage;
  const usageLine = usage ? `${usage.videos_used} of ${usage.videos_per_month} videos this month · ${planName(usage.plan_code)}` : "";
  // Operators only: every video's pipeline runs, all users' (/admin/pipeline).
  const operator = !!me?.user.is_operator;
  const seed = async () => {
    setSeeding(true);
    try {
      await dev.seed();
      await Promise.all([list.reload(), refresh()]);
    } finally {
      setSeeding(false);
    }
  };

  if (list.loading && !list.data) return <Shell brand active="Videos"><div className="p mt24 tiny">Loading…</div></Shell>;
  const inviteBanner = (me?.pending_invites_count ?? 0) > 0 && (
    <div className="inv-banner mt16" style={{ textAlign: "left" }} data-testid="videos-invite-banner">
      <div className="tiny">
        You have <b>{me?.pending_invites_count}</b> pending team {me?.pending_invites_count === 1 ? "invite" : "invites"}.
      </div>
      <Link to="/teams" className="btn dark sm">View invites</Link>
    </div>
  );
  if (items.length === 0) {
    return (
      <Shell brand active="Videos">
        <div className="p center" style={{ paddingTop: "clamp(120px, 26vh, 220px)", maxWidth: 640 }}>
          {inviteBanner}
          <h1 className={inviteBanner ? "mt24" : undefined}>No videos yet.</h1>
          <p className="sub mt16">Fixed camera behind the bowler.<br className="m-only" /> Both creases in view.<br className="m-only" /> Up to 4 hours{desktop ? " per video" : ""}.</p>
          <div className="mt32"><Link className="btn wide" to="/upload">Upload a match</Link></div>
          {operator && <div className="mt10"><Link className="btn ghost wide" to="/admin/pipeline" data-testid="view-pipeline">View pipeline</Link></div>}
          <p className="tiny mt24">Free: 4 videos a month, clips kept 7 days.</p>
          {dev.enabled && <p className="tiny mt24"><button className="u" onClick={seed} disabled={seeding} data-testid="dev-seed">{seeding ? "Seeding…" : "Seed example videos (dev)"}</button></p>}
          <ErrorLine error={list.error} />
        </div>
      </Shell>
    );
  }
  return (
    <Shell brand active="Videos" right={<button className="btn sm m-only" onClick={() => nav("/upload")}>Upload</button>}>
      <div className="p" style={{ paddingTop: desktop ? 48 : 12, paddingBottom: 40 }}>
        <div className="row between">
          <h1 style={desktop ? { fontSize: 32 } : undefined}>Videos</h1>
          <div className="row">
            {operator && <Link className={desktop ? "btn ghost" : "btn ghost sm"} to="/admin/pipeline" data-testid="view-pipeline">View pipeline</Link>}
            <Link className="btn d-only auto" to="/upload">Upload</Link>
          </div>
        </div>
        <p className={desktop ? "sub mt6" : "tiny mt6"}>{usageLine}</p>
        {inviteBanner}
        <ErrorLine error={list.error} />
        <div className={desktop ? "grid3 mt32" : ""}>
          {items.map((v) => <VideoCard key={v.id} v={v} plan={plan} desktop={desktop} />)}
          {desktop && (
            <Link to="/upload" style={{ border: "1px dashed var(--line)", borderRadius: 6, height: 190, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink3)" }}>Upload a match</Link>
          )}
        </div>
      </div>
    </Shell>
  );
}

export function VideoCard({ v, plan, desktop, to, email }: { v: Video; plan: "free" | "basic" | "pro"; desktop: boolean; to?: string; email?: string }) {
  const s = videoStatus(v, plan);
  const ready = v.state === "ready";
  return (
    <Link to={to ?? `/videos/${v.id}`} className={desktop ? "" : "mt24"} style={{ display: "block", minWidth: 0 }} data-testid="video-card" data-state={v.state}>
      <VidFrame height={190} play={ready} poster={v.thumbnail_url} tag={ready && v.clip_count != null ? `${v.clip_count} clips` : undefined} tr={v.team_name ?? undefined} />
      <div className={`row between ${desktop ? "mt16" : "mt10"}`}>
        <div className="med">{v.title}</div>
        <span className="tiny">{fmtDuration(v.duration_ms) || fmtDate(v.created_at)}</span>
      </div>
      {email && <div className="tiny mt6" style={{ overflowWrap: "anywhere" }} data-testid="submission-email">{email}</div>}
      <div className="tiny mt6"><Dot kind={s.dot} />{s.words}{s.detail ? ` · ${s.detail}` : ""}</div>
      <div className="mt6"><Bar value={s.progress} /></div>
    </Link>
  );
}

export function planName(p: string): string {
  return p === "pro" ? "Pro" : p === "basic" ? "Basic" : "Free";
}
